import * as React from 'react'
import { useTranslation, useI18next } from 'gatsby-plugin-react-i18next'
import { EmptyValidator, UrlValidator, MaxLengthValidator } from 'libs/form/validator'
import { useSnackbar } from 'libs/ui/snackbar'
import FormTextField from 'components/presenter/form/FormTextField'
import Form from 'components/presenter/form/Form'
import ChipInput from 'material-ui-chip-input'
import cn from 'classnames'
import { useForm } from 'libs/form/formHook'
import Page from 'components/presenter/page/Page'
import { Paper, TextField } from '@material-ui/core'
import Autocomplete from '@material-ui/lab/Autocomplete'
import categoryData from 'assets/category'
import keywordData from 'assets/keyword'
import newsMetaData from 'assets/newsMeta'
import {
  MuiPickersUtilsProvider,
  KeyboardDatePicker,
  DateTimePicker,
} from '@material-ui/pickers'
import DateFnsUtils from '@date-io/date-fns'
import zhTWLocale from 'date-fns/locale/zh-TW'
import enUSLocale from 'date-fns/locale/en-US'
import _ from 'lodash'
import {
  NonEmptyField,
  EndTimeShouldAfterStartTime,
  InvalidTimePeriod,
} from 'libs/error/errorCodes'
import moment from 'moment'
import * as styles from './create.module.css'

const titleId = 'titleId'
const descriptionId = 'descriptionId'
const websiteId = 'websiteId'
const categoryId = 'categoryId'
const keywordId = 'keywordId'
const promoteStartAtId = 'promoteStartAtId'
const promoteEndAtId = 'promoteEndAtId'
const publishAtId ='publishAtId'
const tagId = 'tagId'
const ageId = 'ageId'
const genderId = 'genderId'
const careId = 'careId'
const voucherId = 'voucherId'
const artistId = 'artistId'

const maxNumOfCategories = 3
const maxNumOfTags = 5
const maxNumOfKeywords = 3

const localeMap = {
  'en_US': enUSLocale,
  'zh_HK': zhTWLocale
}

const FillJetsoDetailForm = ({initFormData, onChanged}) => {
  const { t } = useTranslation()
  const { showError } = useSnackbar()
  const {language} = useI18next()

  const { formState, onChangeValidation, getFormFieldState, isFormReady } = useForm({
    [titleId]: {
      value: initFormData.title,
      validators: [EmptyValidator, MaxLengthValidator(2048)],
    },
    [descriptionId]: {
      isOptional: true,
      value: initFormData.description,
      validators: [EmptyValidator, MaxLengthValidator(2048)],
    },
    [websiteId]: {
      value: initFormData.website,
      isOptional: true,
      validators: [EmptyValidator, UrlValidator],
    },
    [categoryId]: {
      value: initFormData.category,
      validators: [EmptyValidator,],
    },
    [keywordId]: {
      isOptional: true,
      value: initFormData.keyword,
    },
    [careId]: {
      isOptional: true,
      value: initFormData.meta.care,
    },
    [ageId]: {
      isOptional: true,
      value: initFormData.meta.age,
    },
    [genderId]: {
      isOptional: true,
      value: initFormData.meta.gender,
    },
    [voucherId]: {
      isOptional: true,
      value: initFormData.meta.voucher,
    },
    [artistId]: {
      isOptional: true,
      value: initFormData.meta.artist,
    },
    [publishAtId]: {
      value: initFormData.publishAt,
      validators: [EmptyValidator],
    },
    [promoteStartAtId]: {
      isOptional: true,
      value: initFormData.promoteStartAt,
      validators: [EmptyValidator, (newValue, oldValue, fields) => {
        const endAt = fields[promoteEndAtId].value
        if(endAt && !newValue) {
          return NonEmptyField
        }
        
        if (endAt && newValue) {
          if (moment(endAt).isBefore(moment(newValue))) {
            return EndTimeShouldAfterStartTime
          }

          if (moment(endAt).diff(moment(newValue), 'months', true) > 3) {
            return InvalidTimePeriod
          }
        }
        return null
      }],
      dependency: [promoteEndAtId]
    },
    [promoteEndAtId]: {
      isOptional: true,
      value: initFormData.promoteEndAt,
      validators: [EmptyValidator, (newValue, oldValue, fields) => {
        const startAt = fields[promoteStartAtId].value
        if(startAt && !newValue) {
          return NonEmptyField
        }
        
        if (startAt && newValue) {
          if (moment(newValue).isBefore(moment(startAt))) {
            return EndTimeShouldAfterStartTime
          }

          if (moment(newValue).diff(moment(startAt), 'months', true) > 3) {
            return InvalidTimePeriod
          }
        }
        return null
      }],
      dependency: [promoteStartAtId]
    },
    [tagId]: {
      value: initFormData.tags,
      isOptional: true,
    },
  })

  const [categoryOptions, setCategoryOptions] = React.useState([])
  const [keywordOptions, setKeywordOptions] = React.useState([])
  const [voucherOptions, setVoucherOptions] = React.useState([])
  const [artistOptions, setArtistOptions] = React.useState([])
  const [careOptions, setCareOptions] = React.useState([])
  const [genderOptions, setGenderOptions] = React.useState([])
  const [ageOptions, setAgeOptions] = React.useState([])
  
  React.useEffect(() => {
    if (onChanged) {
      onChanged({
        isFormReady: isFormReady(),
        data: {
          title: getFormFieldState(titleId).value,
          description: getFormFieldState(descriptionId).value,
          website: getFormFieldState(websiteId).value,
          category: getFormFieldState(categoryId).value,
          keyword: getFormFieldState(keywordId).value,
          tags: getFormFieldState(tagId).value,
          promoteStartAt: getFormFieldState(promoteStartAtId).value,
          promoteEndAt: getFormFieldState(promoteEndAtId).value,
          publishAt: getFormFieldState(publishAtId).value,
          meta: {
            care: getFormFieldState(careId).value,
            age: getFormFieldState(ageId).value,
            gender: getFormFieldState(genderId).value,
            voucher: getFormFieldState(voucherId).value,
            artist: getFormFieldState(artistId).value,
          }
        }
      })
    }
  }, [formState, isFormReady()])

  React.useEffect(() => {
    const newsMetadataGroup = newsMetaData[language]
    const metaGroup = newsMetadataGroup.filter(group => group.short_code === 'a')
    setVoucherOptions(metaGroup[0].sub_meta[0].sub_meta)
    setArtistOptions(metaGroup[0].sub_meta[1].sub_meta)

    const careGroup = newsMetadataGroup.filter(group => group.short_code === 'b')
    setCareOptions(careGroup[0].sub_meta)
    

    const genderGroup = newsMetadataGroup.filter(group => group.short_code === 'c')
    setGenderOptions(genderGroup[0].sub_meta)

    const ageGroup = newsMetadataGroup.filter(group => group.short_code === 'd')
    setAgeOptions(ageGroup[0].sub_meta)
  }, [])

  React.useEffect(() => {
    const categoryGroup = categoryData[language]
    const _options = _.flatten(
      categoryGroup.map(group => 
        _.flatten(group.sub_category.map(subCat1 => 
          subCat1.sub_category.map(subCat2 => ({
          ...subCat2,
          group: {
            name: `${group.name} / ${subCat1.name}`,
            short_code: subCat1.short_code
          }
        }))))))
    setCategoryOptions(_options)
  }, [])

  React.useEffect(() => {
    const keywordGroup = keywordData[language]
    const _options = _.flatten(
      keywordGroup.map(group => 
        _.flatten(group.sub_keyword.map(sub => 
          ({
            ...sub,
            group: {
              name: group.name,
              short_code: group.short_code
            }
          })))))
    setKeywordOptions(_options)
  }, [])

  const handleAddChips = (chip) => {
    const inputChips = getFormFieldState(tagId).value
    if (inputChips.length >= maxNumOfTags) {
      showError(t('formFields.helper.maxSelect', {
        length: maxNumOfTags
      }))
      return
    }
    onChangeValidation(tagId, [
      ...inputChips,
      chip
    ])
  }

  const handleDeleteChip = (chip, index) => {
    const inputChips = getFormFieldState(tagId).value
    const news = [...inputChips]
    news.splice(index, 1)
    onChangeValidation(tagId, news)
  }

  return (
    <MuiPickersUtilsProvider locale={localeMap[language]} utils={DateFnsUtils}>
      <Page className={styles.root} seoMeta={{
        title: t('jetso.fillJetsoDetail')
      }}>
        <Form className={styles.form}>
          <div className={styles.titleSection}>
            <h1 className={styles.title}>{t('jetso.fillJetsoDetail')}</h1>
            <p className={styles.subTitle}>{t('jetso.fillJetsoDetailDescription')}</p>
          </div>
          <Paper className={styles.paper} elevation={2}>
            <FormTextField
              id={titleId}
              fieldData={getFormFieldState(titleId)}
              className={cn(styles.input)}
              label={t('formFields.title')}
              type="text"
              onChange={onChangeValidation}
            />
            <FormTextField
              id={descriptionId}
              multiline
              fieldData={getFormFieldState(descriptionId)}
              className={cn(styles.input, 'mui-textarea')}
              label={t('formFields.description')}
              type="text"
              onChange={onChangeValidation}
            />
            <Autocomplete
              multiple
              className={cn(styles.input)}
              id={categoryId}
              value={getFormFieldState(categoryId).value}
              options={categoryOptions}
              groupBy={option => option.group.name}
              getOptionLabel={option => option.name}
              disableCloseOnSelect
              filterSelectedOptions
              size="small"
              getOptionDisabled={() => getFormFieldState(categoryId).value.length >= maxNumOfCategories}
              onChange={(event, newValue) => onChangeValidation(categoryId, newValue)}
              noOptionsText={t('formFields.helper.noFound')}
              renderInput={(params) => (
                <TextField
                  {...params}
                  required
                  className="mui-textfield"
                  variant="outlined"
                  label={t('formFields.category')}
                  placeholder={t('formFields.hint.select')}
                  helperText={t('formFields.helper.maxSelect', {
                    length: maxNumOfCategories
                  })}
                />
              )}
            />
            <Autocomplete
              multiple
              id={keywordId}
              value={getFormFieldState(keywordId).value}
              className={cn(styles.input)}
              options={keywordOptions}
              size="small"
              groupBy={option => option.group.name}
              getOptionLabel={option => option.name}
              disableCloseOnSelect
              filterSelectedOptions
              getOptionDisabled={() => getFormFieldState(keywordId).value.length >= maxNumOfKeywords}
              onChange={(event, newValue) => onChangeValidation(keywordId, newValue)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  className="mui-textfield"
                  variant="outlined"
                  label={t('formFields.keyword')}
                  placeholder={t('formFields.hint.select')}
                  helperText={t('formFields.helper.maxSelect', {
                    length: maxNumOfKeywords
                  })}
                />
              )}
            />
            <FormTextField
              id={websiteId}
              fieldData={getFormFieldState(websiteId)}
              className={cn(styles.input)}
              label={t('formFields.website')}
              helperText={t('formFields.jetsoWebsiteHelper')}
              placeholder={t('formFields.hint.urlformat')}
              type="text"
              onChange={onChangeValidation}
            />
            <ChipInput
              className={cn(styles.chipInput, 'mui-textfield')}
              value={getFormFieldState(tagId).value}
              label={t('formFields.tags')}
              variant="outlined"
              onAdd={handleAddChips}
              onDelete={handleDeleteChip}
              placeholder={t('formFields.hint.typeAndEnter')}
              helperText={`${t('formFields.helper.tagsPromote')} (${t('formFields.helper.maxSelect', {
                length: maxNumOfTags
              })})`}
            />
            <KeyboardDatePicker
              id={promoteStartAtId}
              label={t('formFields.promoteStartDate')}
              className={cn(styles.input, 'mui-textfield')}
              format="dd/MM/yyyy"
              value={getFormFieldState(promoteStartAtId).value}
              placeholder="DD/MM/YYYY"
              inputVariant="outlined"
              margin="dense"
              invalidDateMessage={t('errorCodes.10004')}
              helperText={getFormFieldState(promoteStartAtId).hasError ? t(`errorCodes.${getFormFieldState(promoteStartAtId).errorCode}`) : ''}
              error={getFormFieldState(promoteStartAtId).hasError}
              onChange={date => onChangeValidation(promoteStartAtId, date)}
            />
            <KeyboardDatePicker
              id={promoteEndAtId}
              label={t('formFields.promoteEndDate')}
              className={cn(styles.input, 'mui-textfield')}
              disablePast
              format="dd/MM/yyyy"
              value={getFormFieldState(promoteEndAtId).value}
              placeholder="DD/MM/YYYY"
              inputVariant="outlined"
              margin="dense"
              invalidDateMessage={t('errorCodes.10004')}
              helperText={getFormFieldState(promoteEndAtId).hasError ? t(`errorCodes.${getFormFieldState(promoteEndAtId).errorCode}`) : ''}
              error={getFormFieldState(promoteEndAtId).hasError}
              onChange={date => onChangeValidation(promoteEndAtId, date)}
            />
          </Paper>
          <div className={styles.subtitleSection}>
            <p className={styles.subtitle}>{t('jetso.audience')}</p>
          </div>
          <Paper className={styles.paper} elevation={2}>
            <Autocomplete
              multiple
              className={cn(styles.input)}
              id={careId}
              value={getFormFieldState(careId).value}
              options={careOptions}
              getOptionLabel={option => option.name}
              disableCloseOnSelect
              filterSelectedOptions
              size="small"
              onChange={(event, newValue) => onChangeValidation(careId, newValue)}
              noOptionsText={t('formFields.helper.noFound')}
              renderInput={(params) => (
                <TextField
                  {...params}
                  className="mui-textfield"
                  variant="outlined"
                  label={t('formFields.care')}
                  placeholder={t('formFields.hint.select')}
                />
              )}
            />
            <Autocomplete
              multiple
              className={cn(styles.input)}
              id={genderId}
              value={getFormFieldState(genderId).value}
              options={genderOptions}
              getOptionLabel={option => option.name}
              disableCloseOnSelect
              filterSelectedOptions
              size="small"
              onChange={(event, newValue) => onChangeValidation(genderId, newValue)}
              noOptionsText={t('formFields.helper.noFound')}
              renderInput={(params) => (
                <TextField
                  {...params}
                  className="mui-textfield"
                  variant="outlined"
                  label={t('formFields.gender')}
                  placeholder={t('formFields.hint.select')}
                />
              )}
            />
            <Autocomplete
              multiple
              className={cn(styles.input)}
              id={ageId}
              value={getFormFieldState(ageId).value}
              options={ageOptions}
              getOptionLabel={option => option.name}
              disableCloseOnSelect
              filterSelectedOptions
              size="small"
              onChange={(event, newValue) => onChangeValidation(ageId, newValue)}
              noOptionsText={t('formFields.helper.noFound')}
              renderInput={(params) => (
                <TextField
                  {...params}
                  className="mui-textfield"
                  variant="outlined"
                  label={t('formFields.age')}
                  placeholder={t('formFields.hint.select')}
                />
              )}
            />
          </Paper>
          <div className={styles.subtitleSection}>
            <p className={styles.subtitle}>{t('formFields.limited')}</p>
          </div>
          <Paper className={styles.paper} elevation={2}>
            <Autocomplete
              multiple
              className={cn(styles.input)}
              id={voucherId}
              value={getFormFieldState(voucherId).value}
              options={voucherOptions}
              getOptionLabel={option => option.name}
              disableCloseOnSelect
              filterSelectedOptions
              size="small"
              onChange={(event, newValue) => onChangeValidation(voucherId, newValue)}
              noOptionsText={t('formFields.helper.noFound')}
              renderInput={(params) => (
                <TextField
                  {...params}
                  className="mui-textfield"
                  variant="outlined"
                  label={t('formFields.coupon')}
                  placeholder={t('formFields.hint.select')}
                />
              )}
            />
            <Autocomplete
              multiple
              className={cn(styles.input)}
              id={artistId}
              value={getFormFieldState(artistId).value}
              options={artistOptions}
              getOptionLabel={option => option.name}
              disableCloseOnSelect
              filterSelectedOptions
              size="small"
              onChange={(event, newValue) => onChangeValidation(artistId, newValue)}
              noOptionsText={t('formFields.helper.noFound')}
              renderInput={(params) => (
                <TextField
                  {...params}
                  className="mui-textfield"
                  variant="outlined"
                  label={t('formFields.artist')}
                  placeholder={t('formFields.hint.select')}
                />
              )}
            />
          </Paper>
          <div className={styles.subtitleSection}>
            <p className={styles.subtitle}>{t('jetso.publishArrangement')}</p>
          </div>
          <Paper className={styles.paper} elevation={2}>
            <DateTimePicker
              id={publishAtId}
              label={t('formFields.publishTime')}
              className={cn(styles.input, 'mui-textfield')}
              required
              disablePast
              format="dd/MM/yyyy HH:mm"
              value={getFormFieldState(publishAtId).value}
              inputVariant="outlined"
              margin="dense"
              invalidDateMessage={t('errorCodes.10004')}
              helperText={getFormFieldState(publishAtId).hasError ? t(`errorCodes.${getFormFieldState(publishAtId).errorCode}`) : ''}
              error={getFormFieldState(publishAtId).hasError}
              onChange={date => onChangeValidation(publishAtId, date)}
            />
          </Paper>
        </Form>
      </Page>
    </MuiPickersUtilsProvider>
  )
}

export default FillJetsoDetailForm